/**
 * Q0 — the company qualification sheet (one per tenant, managers only): what the
 * analyst knows about the company before proposing anything, following
 * `deploiement-client/methode/qualification.md`. Every line carries a status, a
 * source and a date. The work is progressive: a broad qualification first (every
 * theme, at the surface), then theme cards to go deeper; the output stays this
 * one sheet. The proposal at the end has three parts: configuration, apps and
 * how to use Domino. Lines have stable ids so later patches (the
 * analyst, Q1) target them instead of rewriting the sheet. The analyst also keeps
 * its working hypotheses: its best answer from day one, tested turn after turn.
 */

import { parseRestitution, type QualificationRestitution } from './company-restitution.ts';

export const QUALIFICATION_VERSION = 1 as const;

/** validated = said or confirmed by a person; the analyst never sets it. */
export const QUALIFICATION_STATUSES = ['validated', 'observed', 'hypothesis', 'gap'] as const;
export type QualificationStatus = typeof QUALIFICATION_STATUSES[number];

export const QUALIFICATION_SECTIONS = [
  'product', 'offer', 'site', 'icp', 'competition', 'tools', 'crm', 'workflows',
  'pains', 'objectives', 'people', 'means', 'contradictions', 'reference_examples',
] as const;
export type QualificationSectionKey = typeof QUALIFICATION_SECTIONS[number];

/** Themes deepened one by one after the broad qualification; each groups sections of the sheet. */
export const QUALIFICATION_THEMES = [
  { key: 'company', sections: ['product', 'offer', 'site'] },
  { key: 'icp', sections: ['icp'] },
  { key: 'competition', sections: ['competition'] },
  { key: 'tools', sections: ['tools', 'crm'] },
  { key: 'workflows', sections: ['workflows'] },
  { key: 'pains', sections: ['pains'] },
  { key: 'objectives', sections: ['objectives'] },
  { key: 'team', sections: ['people', 'means'] },
] as const satisfies ReadonlyArray<{ key: string; sections: readonly QualificationSectionKey[] }>;
export type QualificationThemeKey = typeof QUALIFICATION_THEMES[number]['key'];
/** The name of each theme as people see it (the analyst names themes this way too). */
export const QUALIFICATION_THEME_LABELS: Record<QualificationThemeKey, string> = {
  company: 'Entreprise et offre',
  icp: 'Cible et acheteurs',
  competition: 'Concurrence',
  tools: 'Outils et données',
  workflows: 'Workflows',
  pains: 'Points de douleur',
  objectives: 'Objectifs et mesures',
  team: 'Équipe et adoption',
};
/** Sections outside the themes: they gather the whole analysis before the proposal. */
export const SYNTHESIS_SECTIONS = ['contradictions', 'reference_examples'] as const satisfies readonly QualificationSectionKey[];

export const THEME_DEPTHS = ['surface', 'in_progress', 'deep'] as const;
export type ThemeDepth = typeof THEME_DEPTHS[number];
export interface QualificationTheme {
  depth: ThemeDepth;
  /** Short synthesis of the theme, in the client's words. */
  summary: string;
}

/**
 * A working hypothesis: what the analyst believes limits the company's revenue
 * or should shape the proposal, and what would confirm or refute it. Decided
 * only by a reading or a person; a refuted one stays visible (it is progress).
 */
export const HYPOTHESIS_STATES = ['open', 'confirmed', 'refuted'] as const;
export type HypothesisState = typeof HYPOTHESIS_STATES[number];
export interface WorkingHypothesis {
  id: string;
  text: string;
  /** What would confirm or refute it. */
  test: string;
  state: HypothesisState;
  /** What decided it: a reading, the person's answer, or a manual change. */
  evidence?: QualificationSource;
  /** ISO date (YYYY-MM-DD) of its last change. */
  date: string;
}

export const BROAD_STATES = ['not_started', 'in_progress', 'done'] as const;
export type BroadState = typeof BROAD_STATES[number];

export const QUALIFICATION_SOURCE_KINDS = ['page', 'search', 'crm', 'registry', 'document', 'answer', 'manual'] as const;
export type QualificationSourceKind = typeof QUALIFICATION_SOURCE_KINDS[number];

/** Destinations of `guide-audit.md`: context, playbook, objectives, CRM, targets, app, measures. */
export const QUALIFICATION_DESTINATIONS = ['CM', 'PB', 'OBJ', 'CRM', 'CIB', 'APP', 'MES'] as const;
export type QualificationDestination = typeof QUALIFICATION_DESTINATIONS[number];

export interface QualificationSource {
  kind: QualificationSourceKind;
  label: string;
  /** URL, CRM object, document id… — whatever lets someone open it again. */
  ref?: string;
  excerpt?: string;
}

export interface QualificationItem {
  id: string;
  text: string;
  status: QualificationStatus;
  source?: QualificationSource;
  /** ISO date (YYYY-MM-DD) of the fact or of its last change. */
  date: string;
  destinations?: QualificationDestination[];
}

export const PROPOSAL_DECISIONS = ['proposed', 'accepted', 'dismissed'] as const;
export type ProposalDecision = typeof PROPOSAL_DECISIONS[number];

/**
 * Plan of an accepted proposal (08/10/2026): when to do it, and when it was done.
 * « Plus tard » is an accepted proposal with the stage « later ».
 */
export const PLAN_STAGES = ['now', 'soon', 'later'] as const;
export type PlanStage = typeof PLAN_STAGES[number];
export interface PlanState {
  stage?: PlanStage;
  /** ISO date it was done (by hand, or by the change Domino applied). */
  doneAt?: string;
}

/**
 * « Mise en place » (13/10/2026): the analyst's proposals laid out as a pipeline
 * of stages the manager works through in any order, and may skip.
 */
export const SETUP_STAGES = ['connections', 'profile', 'crm', 'apps', 'rituals'] as const;
export type SetupStage = typeof SETUP_STAGES[number];
export interface SetupState {
  /** Stages the manager chose to skip; they stay open to come back to. */
  skipped: SetupStage[];
}

export const CONFIGURATION_AREAS = ['profile', 'objectives', 'crm', 'connections', 'documents'] as const;
export type ConfigurationArea = typeof CONFIGURATION_AREAS[number];

export interface ConfigurationProposal extends PlanState {
  id: string;
  area: ConfigurationArea;
  text: string;
  why?: string;
  decision: ProposalDecision;
}

export interface AppProposal extends PlanState {
  id: string;
  title: string;
  /** The bottleneck it serves, its cost, how success is measured, the reference set it is judged on. */
  bottleneck: string;
  cost?: string;
  measure: string;
  truthSet?: string;
  expected?: string;
  decision: ProposalDecision;
}

export const USAGE_RHYTHMS = ['daily', 'weekly', 'monthly', 'once'] as const;
export type UsageRhythm = typeof USAGE_RHYTHMS[number];
export const USAGE_PLACES = ['today', 'accounts', 'apps', 'chat', 'forge', 'profile', 'objectives'] as const;
export type UsagePlace = typeof USAGE_PLACES[number];
/**
 * 13/10/2026: a ritual is a recurring moment at a fixed time, held by a person
 * (it goes in their tasks in Aujourd'hui); a reflex is done at an event (« before each
 * demo »), for Domino to bring up or do at that moment. A one-time action is a
 * configuration line, not a usage (older sheets keep rhythm « once »).
 */
export const USAGE_KINDS = ['ritual', 'reflex'] as const;
export type UsageKind = typeof USAGE_KINDS[number];

export interface UsageProposal extends PlanState {
  id: string;
  kind: UsageKind;
  /** The ritual added to Aujourd'hui from it (workspace_rituals.id). */
  ritualId?: string;
  rhythm: UsageRhythm;
  place: UsagePlace;
  who: string;
  what: string;
  decision: ProposalDecision;
}

export interface QualificationProposal {
  configuration: ConfigurationProposal[];
  apps: AppProposal[];
  usage: UsageProposal[];
  /** Verifiable, dated success criterion agreed with the client. */
  success: string;
  firstProof: string;
  notProposed: string;
}

export interface CompanyQualificationSheet {
  version: typeof QUALIFICATION_VERSION;
  /** The first, essential step: one pass over every theme. */
  broad: { state: BroadState };
  hypotheses: WorkingHypothesis[];
  themes: Record<QualificationThemeKey, QualificationTheme>;
  sections: Record<QualificationSectionKey, QualificationItem[]>;
  proposal: QualificationProposal;
  /** What the manager reads: the answer, then a page per theme (company-restitution.ts). */
  restitution: QualificationRestitution;
  setup: SetupState;
}

const ID = /^[a-z][a-z0-9_-]{0,63}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max = 4000): string | null =>
  typeof value === 'string' && value.trim() && value.trim().length <= max ? value.trim() : null;
const optionalText = (value: unknown, max = 4000): string | undefined => text(value, max) ?? undefined;
const oneOf = <T extends string>(values: readonly T[], value: unknown): T | null =>
  typeof value === 'string' && (values as readonly string[]).includes(value) ? value as T : null;
const id = (value: unknown): string | null => typeof value === 'string' && ID.test(value) ? value : null;

export const emptyQualificationSheet = (): CompanyQualificationSheet => ({
  version: QUALIFICATION_VERSION,
  broad: { state: 'not_started' },
  hypotheses: [],
  themes: Object.fromEntries(QUALIFICATION_THEMES.map(({ key }) => [key, { depth: 'surface', summary: '' }])) as unknown as CompanyQualificationSheet['themes'],
  sections: Object.fromEntries(QUALIFICATION_SECTIONS.map((key) => [key, []])) as unknown as CompanyQualificationSheet['sections'],
  proposal: { configuration: [], apps: [], usage: [], success: '', firstProof: '', notProposed: '' },
  restitution: { pages: {} },
  setup: { skipped: [] },
});

/** A new stable id for a line, unique in the sheet. */
export const newQualificationId = (prefix: string, taken: ReadonlySet<string>): string => {
  for (let index = 1; ; index += 1) {
    const candidate = `${prefix}-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
};

export const qualificationIds = (sheet: CompanyQualificationSheet): Set<string> => new Set([
  ...sheet.hypotheses.map((hypothesis) => hypothesis.id),
  ...QUALIFICATION_SECTIONS.flatMap((key) => sheet.sections[key].map((item) => item.id)),
  ...sheet.proposal.configuration.map((item) => item.id),
  ...sheet.proposal.apps.map((item) => item.id),
  ...sheet.proposal.usage.map((item) => item.id),
]);

const parseSource = (value: unknown): QualificationSource | undefined => {
  if (!isRecord(value)) return undefined;
  const kind = oneOf(QUALIFICATION_SOURCE_KINDS, value.kind);
  const label = text(value.label, 300);
  if (!kind || !label) return undefined;
  return { kind, label, ref: optionalText(value.ref, 2000), excerpt: optionalText(value.excerpt, 2000) };
};

const parseItem = (value: unknown): QualificationItem | null => {
  if (!isRecord(value)) return null;
  const itemId = id(value.id);
  const itemText = text(value.text);
  const status = oneOf(QUALIFICATION_STATUSES, value.status);
  const date = typeof value.date === 'string' && DATE.test(value.date) ? value.date : null;
  if (!itemId || !itemText || !status || !date) return null;
  const destinations = Array.isArray(value.destinations)
    ? value.destinations.flatMap((entry) => oneOf(QUALIFICATION_DESTINATIONS, entry) ?? [])
    : [];
  return { id: itemId, text: itemText, status, source: parseSource(value.source), date, destinations: destinations.length ? destinations : undefined };
};

const parseHypothesis = (value: unknown): WorkingHypothesis | null => {
  if (!isRecord(value)) return null;
  const hypothesisId = id(value.id); const hypothesisText = text(value.text, 1000); const test = text(value.test, 1000);
  const state = oneOf(HYPOTHESIS_STATES, value.state);
  const date = typeof value.date === 'string' && DATE.test(value.date) ? value.date : null;
  if (!hypothesisId || !hypothesisText || !test || !state || !date) return null;
  return { id: hypothesisId, text: hypothesisText, test, state, evidence: parseSource(value.evidence), date };
};

const decision = (value: unknown): ProposalDecision => oneOf(PROPOSAL_DECISIONS, value) ?? 'proposed';
const plan = (entry: Record<string, unknown>): PlanState => {
  const stage = oneOf(PLAN_STAGES, entry.stage);
  const doneAt = typeof entry.doneAt === 'string' && DATE.test(entry.doneAt) ? entry.doneAt : null;
  return { ...(stage ? { stage } : {}), ...(doneAt ? { doneAt } : {}) };
};

const list = <T>(value: unknown, parse: (entry: unknown) => T | null): T[] =>
  Array.isArray(value) ? value.flatMap((entry) => parse(entry) ?? []) : [];

/**
 * Tolerant read of a stored sheet: an invalid line is dropped (and counted),
 * never replaced by something else; an absent sheet reads as empty.
 */
export const parseQualificationSheet = (value: unknown): { sheet: CompanyQualificationSheet; dropped: number } => {
  const sheet = emptyQualificationSheet();
  if (!isRecord(value)) return { sheet, dropped: 0 };
  let dropped = 0;
  if (isRecord(value.broad)) sheet.broad.state = oneOf(BROAD_STATES, value.broad.state) ?? 'not_started';
  sheet.hypotheses = list(value.hypotheses, parseHypothesis);
  if (Array.isArray(value.hypotheses)) dropped += value.hypotheses.length - sheet.hypotheses.length;
  const themes = isRecord(value.themes) ? value.themes : {};
  for (const { key } of QUALIFICATION_THEMES) {
    const theme = themes[key];
    if (isRecord(theme)) sheet.themes[key] = { depth: oneOf(THEME_DEPTHS, theme.depth) ?? 'surface', summary: text(theme.summary) ?? '' };
  }
  const count = (raw: unknown, kept: unknown[]) => { if (Array.isArray(raw)) dropped += raw.length - kept.length; };
  const sections = isRecord(value.sections) ? value.sections : {};
  for (const key of QUALIFICATION_SECTIONS) {
    sheet.sections[key] = list(sections[key], parseItem);
    count(sections[key], sheet.sections[key]);
  }
  const proposal = isRecord(value.proposal) ? value.proposal : {};
  sheet.proposal.configuration = list(proposal.configuration, (entry) => {
    if (!isRecord(entry)) return null;
    const entryId = id(entry.id); const area = oneOf(CONFIGURATION_AREAS, entry.area); const entryText = text(entry.text);
    return entryId && area && entryText ? { id: entryId, area, text: entryText, why: optionalText(entry.why), decision: decision(entry.decision), ...plan(entry) } : null;
  });
  sheet.proposal.apps = list(proposal.apps, (entry) => {
    if (!isRecord(entry)) return null;
    const entryId = id(entry.id); const title = text(entry.title, 160); const bottleneck = text(entry.bottleneck); const measure = text(entry.measure);
    return entryId && title && bottleneck && measure ? {
      id: entryId, title, bottleneck, measure, cost: optionalText(entry.cost), truthSet: optionalText(entry.truthSet),
      expected: optionalText(entry.expected), decision: decision(entry.decision), ...plan(entry),
    } : null;
  });
  sheet.proposal.usage = list(proposal.usage, (entry) => {
    if (!isRecord(entry)) return null;
    const entryId = id(entry.id); const rhythm = oneOf(USAGE_RHYTHMS, entry.rhythm); const place = oneOf(USAGE_PLACES, entry.place);
    const who = text(entry.who, 300); const what = text(entry.what);
    const kind = oneOf(USAGE_KINDS, entry.kind) ?? 'ritual';
    const ritualId = typeof entry.ritualId === 'string' && /^[0-9a-f-]{36}$/.test(entry.ritualId) ? entry.ritualId : null;
    return entryId && rhythm && place && who && what
      ? { id: entryId, kind, ...(ritualId ? { ritualId } : {}), rhythm, place, who, what, decision: decision(entry.decision), ...plan(entry) }
      : null;
  });
  count(proposal.configuration, sheet.proposal.configuration);
  count(proposal.apps, sheet.proposal.apps);
  count(proposal.usage, sheet.proposal.usage);
  sheet.proposal.success = text(proposal.success) ?? '';
  sheet.proposal.firstProof = text(proposal.firstProof) ?? '';
  sheet.proposal.notProposed = text(proposal.notProposed) ?? '';
  sheet.restitution = parseRestitution(value.restitution);
  const skipped = isRecord(value.setup) && Array.isArray(value.setup.skipped) ? value.setup.skipped : [];
  sheet.setup = { skipped: SETUP_STAGES.filter((stage) => skipped.includes(stage)) };
  return { sheet, dropped };
};
